import { useStyletron } from '@thanx/uikit/theme'
import ModalContainer from 'components/ModalContainer'
import palette from 'constants/palette'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import { buildTranslate } from 'locales'
import type { Fields as Location } from 'models/Location'
import React from 'react'
import usePhoneField from './PhoneField'

const t = buildTranslate('locations.location_list.details_modal')
const tShared = buildTranslate('shared_words')

// Matches Figma 432:2542. Section blocks separated by horizontal
// dividers. The phone row's inline Edit toggle (swap value for input +
// Save, no second modal) lives inside `usePhoneField` so this component
// stays a flat composition of read-only sections — keeps the modal's
// cyclomatic complexity in line with the rest of the OC surface.

type Props = {
  isOpen: boolean
  onClose: () => void
  location: Location
}

const Section = ({
  label,
  children,
  trailing,
}: {
  label: string
  children: React.ReactNode
  trailing?: React.ReactNode
}) => {
  const [css] = useStyletron()
  return (
    <div
      className={`d-flex align-items-center ${css({
        gap: '24px',
        width: '100%',
      })}`}
    >
      <div className={css({ flex: '1 0 0', minWidth: 0 })}>
        <p
          className={`mb-xs ${css({
            color: palette.grey90,
            fontFamily: 'Lato, sans-serif',
            fontWeight: 700,
            fontSize: '18px',
            lineHeight: '24px',
            letterSpacing: '0.25px',
            margin: 0,
            marginBottom: '8px',
          })}`}
        >
          {label}
        </p>
        <div
          className={css({
            color: palette.grey70,
            fontFamily: 'Lato, sans-serif',
            fontSize: '16px',
            lineHeight: '24px',
            letterSpacing: '0.25px',
          })}
        >
          {children}
        </div>
      </div>
      {trailing}
    </div>
  )
}

const Divider = () => {
  const [css] = useStyletron()
  return (
    <div
      className={css({
        height: '1px',
        backgroundColor: palette.grey20,
        width: '100%',
      })}
    />
  )
}

const LocationDetailsModal = ({ isOpen, onClose, location }: Props) => {
  const [css] = useStyletron()
  const merchant = useCurrentMerchant()
  const phone = usePhoneField(location)
  const orderingUrl = `https://order.thanx.com/${merchant?.handle}?location=${location.id}`

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={onClose}
      onProceed={onClose}
      proceedText={tShared('close')}
      hideCancel
      showBottomProceed
      // Float above OC's main { z-index: 700 } and the top-nav dropdowns
      // (z-2000). See LocationsTab for the rationale.
      zIndex={3000}
      ariaLabel={t('aria_label', { name: location.name })}
    >
      <h3
        className={`mt-0 ${css({
          color: palette.grey90,
          fontFamily: 'Lato, sans-serif',
          fontWeight: 900,
          fontSize: '24px',
          lineHeight: '28px',
          letterSpacing: '0.25px',
          marginBottom: '24px',
        })}`}
      >
        {t('title')}
      </h3>

      <div
        className={`d-flex flex-column ${css({
          gap: '24px',
          width: '100%',
        })}`}
      >
        <Section label={t('address')}>
          <p className="m-0">{location.street}</p>
          <p className="m-0 mt-xxs">
            {location.city}, {location.state} {location.zip}
          </p>
        </Section>
        <Divider />
        <Section label={t('phone_number')} trailing={phone.trailing}>
          {phone.body}
        </Section>
        <Divider />
        <Section label={t('ordering_url')}>{orderingUrl}</Section>
        <Divider />
        <Section label={t('timezone')}>{location.time_zone}</Section>
        {location.external_uid && (
          <>
            <Divider />
            <Section label={t('external_id')}>{location.external_uid}</Section>
          </>
        )}
      </div>
    </ModalContainer>
  )
}

export default LocationDetailsModal
